import { useState, useEffect } from 'react'
import { workOrdersApi } from '../../services/api'
import toast from 'react-hot-toast'
import { Send, X, Search } from 'lucide-react'

const DESTINATARI_DEFAULT = [
  'ANDREA.CUSINA@POSTEITALIANE.IT',
  'rodolfo.conte@italtel.com',
  'stefano.sborchia@deltaict.it',
  'backoffice@deltaict.it',
].join('\n')

interface PraticaInfo {
  wo_id: number
  numero_progressivo: string
  codice_sito: string
  denominazione_sito: string
  regione: string
  provincia: string
  comune: string
  indirizzo: string
  data_appuntamento: string
  slot_orario: string
  tecnico_nome: string
}

interface Props {
  pratichePreselezionate: PraticaInfo[]
  onClose: () => void
}

function buildCorpo(p: PraticaInfo): string {
  const sito = [p.denominazione_sito, p.regione,
    p.provincia ? `(${p.provincia})` : '', p.comune, p.indirizzo]
    .filter(Boolean).join(' ')
  const appStr = p.data_appuntamento
    ? ` - App. ${p.data_appuntamento}${p.slot_orario ? ' ' + p.slot_orario : ''}`
    : ''
  const ora = new Date().toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
  return (
    `Buongiorno Andrea,\n\n` +
    `sul sito di ${sito}${appStr}\n` +
    `non hanno ricevuto la comunicazione per gli accessi.\n\n` +
    `Tecnico: ${p.tecnico_nome || '—'}\n\n` +
    `Grazie\n\n` +
    `${ora}\n` +
    `Delta c/o Italtel S.p.a.\n\n` +
    `---\n` +
    `(In caso di risposta, inviare per conoscenza a: backoffice@deltaict.it)`
  )
}

function buildOggetto(p: PraticaInfo): string {
  return `ACCESSO NON CONSENTITO - Frazionario ${p.codice_sito} - ${p.denominazione_sito}`
}

function buildOggettoMultiplo(pratiche: PraticaInfo[]): string {
  if (pratiche.length === 1) return buildOggetto(pratiche[0])
  const frazionari = pratiche.map(p => p.codice_sito).filter(Boolean).join(', ')
  return `ACCESSO NON CONSENTITO - ${pratiche.length} siti - Frazionari ${frazionari}`
}

function buildCorpoMultiplo(pratiche: PraticaInfo[]): string {
  if (pratiche.length === 1) return buildCorpo(pratiche[0])
  const ora = new Date().toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
  const righe = pratiche.map((p, i) => {
    const sito = [p.denominazione_sito, p.regione,
      p.provincia ? `(${p.provincia})` : '', p.comune, p.indirizzo]
      .filter(Boolean).join(' ')
    const appStr = p.data_appuntamento
      ? ` - App. ${p.data_appuntamento}${p.slot_orario ? ' ' + p.slot_orario : ''}`
      : ''
    return `${i + 1}) Frazionario ${p.codice_sito} - ${sito}${appStr} - Tecnico: ${p.tecnico_nome || '—'}`
  }).join('\n')
  return (
    `Buongiorno Andrea,\n\n` +
    `sui seguenti siti non hanno ricevuto la comunicazione per gli accessi:\n\n` +
    `${righe}\n\n` +
    `Grazie\n\n` +
    `${ora}\n` +
    `Delta c/o Italtel S.p.a.\n\n` +
    `---\n` +
    `(In caso di risposta, inviare per conoscenza a: backoffice@deltaict.it)`
  )
}

// ─── Modalità singola / ricerca ────────────────────────────────────────────────

function SingleMode({ preselezionata, onClose }: { preselezionata: PraticaInfo | null; onClose: () => void }) {
  const [frazionario, setFrazionario] = useState(preselezionata?.codice_sito || '')
  const [pratica, setPratica] = useState<PraticaInfo | null>(preselezionata)
  const [cercaLoading, setCercaLoading] = useState(false)
  const [invioLoading, setInvioLoading] = useState(false)
  const [inviata, setInviata] = useState(false)
  const [destinatari, setDestinatari] = useState(DESTINATARI_DEFAULT)
  const [oggetto, setOggetto] = useState('')
  const [corpo, setCorpo] = useState('')

  useEffect(() => {
    if (pratica) {
      setOggetto(buildOggetto(pratica))
      setCorpo(buildCorpo(pratica))
    }
  }, [pratica])

  const cerca = async () => {
    if (!frazionario.trim()) return
    setCercaLoading(true)
    try {
      const res = await workOrdersApi.byFrazionario(frazionario.trim())
      setPratica(res.data)
    } catch {
      toast.error(`Nessuna pratica trovata per frazionario "${frazionario}"`)
      setPratica(null)
    } finally {
      setCercaLoading(false)
    }
  }

  const invia = async () => {
    if (!pratica) return
    const emails = destinatari.split(/[,;\n]/).map(e => e.trim()).filter(Boolean)
    if (!emails.length) { toast.error('Inserire almeno un destinatario'); return }
    setInvioLoading(true)
    try {
      await workOrdersApi.inviaEmailAccessi({
        ...pratica,
        destinatari: emails,
        oggetto_custom: oggetto,
        corpo_custom: corpo,
      } as any)
      setInviata(true)
      toast.success('Email inviata correttamente')
      setTimeout(onClose, 1500)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore invio email')
    } finally {
      setInvioLoading(false)
    }
  }

  return (
    <div className="p-5 space-y-4">
      {/* Input frazionario */}
      <div>
        <label className="label">Frazionario (Codice Sito)</label>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="es. 93146"
            value={frazionario}
            onChange={e => { setFrazionario(e.target.value); if (!preselezionata) setPratica(null) }}
            onKeyDown={e => e.key === 'Enter' && cerca()}
            disabled={!!preselezionata}
          />
          {!preselezionata && (
            <button
              onClick={cerca}
              disabled={!frazionario.trim() || cercaLoading}
              className="btn-primary flex items-center gap-1.5 px-4"
            >
              <Search size={14} />
              {cercaLoading ? '...' : 'Cerca'}
            </button>
          )}
        </div>
      </div>

      {pratica && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs">
          <div className="font-semibold text-blue-800 mb-2">Pratica trovata</div>
          <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
            {([
              ['N° Pratica', pratica.numero_progressivo],
              ['Frazionario', pratica.codice_sito],
              ['Denominazione', pratica.denominazione_sito || '—'],
              ['Comune', `${pratica.comune || '—'}${pratica.provincia ? ` (${pratica.provincia})` : ''}`],
              ['Appuntamento', `${pratica.data_appuntamento || '—'} ${pratica.slot_orario || ''}`],
              ['Tecnico', pratica.tecnico_nome || '—'],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="contents">
                <span className="text-slate-500">{k}</span>
                <span className="font-medium text-slate-800">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="label">
          Destinatari
          <span className="text-slate-400 font-normal ml-1">(uno per riga o separati da virgola)</span>
        </label>
        <textarea rows={4} className="input w-full font-mono text-xs" value={destinatari} onChange={e => setDestinatari(e.target.value)} />
      </div>

      <div>
        <label className="label">Oggetto</label>
        <input className="input w-full" value={oggetto} onChange={e => setOggetto(e.target.value)} placeholder={pratica ? buildOggetto(pratica) : 'Seleziona prima una pratica'} />
      </div>

      <div>
        <label className="label">Testo email</label>
        <textarea
          rows={10}
          className="input w-full text-xs"
          style={{ fontFamily: 'inherit', lineHeight: '1.6' }}
          value={corpo}
          onChange={e => setCorpo(e.target.value)}
          placeholder={pratica ? '' : 'Seleziona prima una pratica per pre-compilare il testo'}
        />
      </div>

      <div className="flex gap-3 pt-1">
        <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
        <button
          onClick={invia}
          disabled={!pratica || !oggetto.trim() || !corpo.trim() || invioLoading || inviata}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
        >
          <Send size={14} />
          {inviata ? '✓ Inviata' : invioLoading ? 'Invio...' : 'Invia Email'}
        </button>
      </div>
    </div>
  )
}

// ─── Modalità bulk (un'unica email con tutte le pratiche elencate) ─────────────

function BulkMode({ pratiche, onClose }: { pratiche: PraticaInfo[]; onClose: () => void }) {
  const [destinatari, setDestinatari] = useState(DESTINATARI_DEFAULT)
  // Set di wo_id da includere nell'invio (tutti selezionati di default)
  const [inclusi, setInclusi] = useState<Set<number>>(new Set(pratiche.map(p => p.wo_id)))
  const [invioLoading, setInvioLoading] = useState(false)
  const [inviata, setInviata] = useState(false)
  const [oggettoDraft, setOggettoDraft] = useState<string | null>(null)
  const [corpoDraft, setCorpoDraft] = useState<string | null>(null)

  const toggleIncluso = (wo_id: number) => {
    setInclusi(prev => {
      const next = new Set(prev)
      if (next.has(wo_id)) next.delete(wo_id)
      else next.add(wo_id)
      return next
    })
    // Le modifiche manuali a oggetto/corpo vanno perse se cambia la selezione
    // (altrimenti resterebbero riferiti a un elenco pratiche non più valido).
    setOggettoDraft(null)
    setCorpoDraft(null)
  }

  const selezionaTutte = () => { setInclusi(new Set(pratiche.map(p => p.wo_id))); setOggettoDraft(null); setCorpoDraft(null) }
  const deselezionaTutte = () => { setInclusi(new Set()); setOggettoDraft(null); setCorpoDraft(null) }

  const praticheSelezionate = pratiche.filter(p => inclusi.has(p.wo_id))
  const oggetto = oggettoDraft ?? (praticheSelezionate.length ? buildOggettoMultiplo(praticheSelezionate) : '')
  const corpo = corpoDraft ?? (praticheSelezionate.length ? buildCorpoMultiplo(praticheSelezionate) : '')

  const invia = async () => {
    const emails = destinatari.split(/[,;\n]/).map(e => e.trim()).filter(Boolean)
    if (!emails.length) { toast.error('Inserire almeno un destinatario'); return }
    if (!praticheSelezionate.length) { toast.error('Nessuna pratica selezionata'); return }

    setInvioLoading(true)
    try {
      await workOrdersApi.inviaEmailAccessi({
        ...praticheSelezionate[0],
        destinatari: emails,
        oggetto_custom: oggetto,
        corpo_custom: corpo,
      } as any)
      setInviata(true)
      toast.success(`Email inviata con ${praticheSelezionate.length} pratiche`)
      setTimeout(onClose, 1500)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore invio email')
    } finally {
      setInvioLoading(false)
    }
  }

  return (
    <div className="p-5 space-y-4">
      {/* Destinatari */}
      <div>
        <label className="label">
          Destinatari
          <span className="text-slate-400 font-normal ml-1">(uno per riga o separati da virgola)</span>
        </label>
        <textarea
          rows={3}
          className="input w-full font-mono text-xs"
          value={destinatari}
          onChange={e => setDestinatari(e.target.value)}
          disabled={invioLoading || inviata}
        />
      </div>

      {/* Lista pratiche */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label mb-0">
            Pratiche incluse nell'email ({inclusi.size} di {pratiche.length})
          </label>
          {!invioLoading && !inviata && (
            <div className="flex gap-2 text-xs">
              <button onClick={selezionaTutte} className="text-blue-600 hover:underline">Tutte</button>
              <span className="text-slate-300">|</span>
              <button onClick={deselezionaTutte} className="text-slate-500 hover:underline">Nessuna</button>
            </div>
          )}
        </div>
        <div className="border rounded-lg divide-y max-h-64 overflow-y-auto text-xs">
          {pratiche.map(p => {
            const selezionata = inclusi.has(p.wo_id)
            return (
              <div
                key={p.wo_id}
                className={`flex items-start gap-3 p-2.5 ${selezionata ? '' : 'opacity-40'}`}
              >
                <input
                  type="checkbox"
                  checked={selezionata}
                  onChange={() => toggleIncluso(p.wo_id)}
                  disabled={invioLoading || inviata}
                  className="cursor-pointer mt-0.5 flex-shrink-0"
                />

                {/* Info pratica */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-medium text-blue-700">{p.codice_sito || '—'}</span>
                    <span className="text-slate-600 truncate">{p.denominazione_sito || '—'}</span>
                  </div>
                  <div className="text-slate-400 mt-0.5 flex gap-3 flex-wrap">
                    <span>{p.comune}{p.provincia ? ` (${p.provincia})` : ''}</span>
                    {p.data_appuntamento && <span>App. {p.data_appuntamento}{p.slot_orario ? ' ' + p.slot_orario : ''}</span>}
                    {p.tecnico_nome && <span>👷 {p.tecnico_nome}</span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div>
        <label className="label">Oggetto</label>
        <input
          className="input w-full"
          value={oggetto}
          onChange={e => setOggettoDraft(e.target.value)}
          disabled={invioLoading || inviata}
        />
      </div>

      <div>
        <label className="label">Testo email</label>
        <textarea
          rows={10}
          className="input w-full text-xs"
          style={{ fontFamily: 'inherit', lineHeight: '1.6' }}
          value={corpo}
          onChange={e => setCorpoDraft(e.target.value)}
          disabled={invioLoading || inviata}
        />
      </div>

      {/* Footer */}
      <div className="flex gap-3 pt-1">
        <button onClick={onClose} className="btn-secondary flex-1">
          {inviata ? 'Chiudi' : 'Annulla'}
        </button>
        <button
          onClick={invia}
          disabled={invioLoading || inviata || inclusi.size === 0 || !oggetto.trim() || !corpo.trim()}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
        >
          <Send size={14} />
          {inviata ? '✓ Inviata' : invioLoading ? 'Invio...' : `Invia 1 email con ${inclusi.size} pratiche`}
        </button>
      </div>
    </div>
  )
}

// ─── Modal wrapper ─────────────────────────────────────────────────────────────

export default function EmailAccessiModal({ pratichePreselezionate, onClose }: Props) {
  const isBulk = pratichePreselezionate.length > 1
  const single = pratichePreselezionate.length === 1 ? pratichePreselezionate[0] : null

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className={`bg-white rounded-xl shadow-xl w-full max-h-[90vh] overflow-y-auto ${isBulk ? 'max-w-2xl' : 'max-w-lg'}`}>

        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold">✉️ Email Accessi Non Consentiti</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Da <span className="font-medium">wfm@deltaict.it</span> · Reply-To: backoffice@deltaict.it
              {isBulk && (
                <span className="ml-2 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                  {pratichePreselezionate.length} pratiche
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {isBulk
          ? <BulkMode pratiche={pratichePreselezionate} onClose={onClose} />
          : <SingleMode preselezionata={single} onClose={onClose} />
        }
      </div>
    </div>
  )
}
